/* eslint-disable function-paren-newline */

import {
  IDealDetailsResponseDataRaw,
  IDealDetails,
  IFormattedAttribute,
} from '../../types';

import { formatGradeReviewRequestDetailsResponse } from './formatGradeReviewRequestDetailsResponse';
import { formatStatus } from './utils';

/**
 * This transforms the `raw deal details API response` to the format that
 * the UI expects.
 */
export function formatDealDetailsResponse(
  response: IDealDetailsResponseDataRaw
): IDealDetails {
  const {
    appraisalData,
    assetInfo,
    brandName,
    categoryName,
    outRightSaleInfo,
    gradeReviewRequestData,
    isAutomatedAssessment,
  } = response || {};

  const {
    appraisalCode,
    approvedDeal,
    assetImageUrls = [],
    awbNumber: shipmentNumber,
    awbTrackingURL: shipmentTrackingUrl,
    courierStatus,
    dealTS: dealDate,
    deliveryDate,
    displayStatus: statusLabels,
    invoices,
    isDelivered,
    isReturned,
    paymentType,
    serialNumber: imeiNumber,
    totalAmount,
    VATType,
  } = appraisalData || {};

  const purchasePricesRaw = outRightSaleInfo?.outRightsale || [];
  const { assetPrice: myBidOnDealDate, assetCondition } = approvedDeal || {};
  const { label, attributes } = assetInfo || {};
  const formattedVatType = VATType ? formatStatus(VATType) : '';

  /**
   * Builds formatted attributes with localized keys and values.
   * Falls back to English if a language variant is missing.
   */
  const buildFormattedAttributes = (): IFormattedAttribute[] => {
    if (!attributes || attributes.length === 0) {
      return [];
    }

    return attributes.map((attr) => {
      const localizedKey = {
        en: attr.key,
        ar: attr.key_ar || attr.key,
        de: attr.key_de || attr.key,
        es: attr.key_es || attr.key,
        fr: attr.key_fr || attr.key,
        he: attr.key_he || attr.key,
        nl: attr.key_nl || attr.key,
        ro: attr.key_ro || attr.key,
      };

      const localizedValue = {
        en: attr.value,
        ar: attr.value_ar || attr.value,
        de: attr.value_de || attr.value,
        es: attr.value_es || attr.value,
        fr: attr.value_fr || attr.value,
        he: attr.value_he || attr.value,
        nl: attr.value_nl || attr.value,
        ro: attr.value_ro || attr.value,
      };

      return {
        key: localizedKey,
        value: localizedValue,
      };
    });
  };

  const { condition, dealerCondition, percentValue, dealerPercentValue } =
    assetCondition?.memberCondition || {};
  const mobileGrade = dealerCondition || condition;

  const storage =
    attributes?.find((attr) => attr.key === 'Storage')?.value || '-';
  const deviceModel =
    attributes?.find((attr) => attr.key === 'Model')?.value || '-';

  let priceMyPurchasePrice = 0;
  let priceCourierFee = 0;
  let priceCourierFeeIncVAT = 0;

  purchasePricesRaw?.forEach((price) => {
    const { value: priceValue = '', label: priceLabel = '' } = price || {};
    const value = priceValue.replace(/,/g, '') || '0';

    if (priceLabel.includes('My Purchase price')) {
      priceMyPurchasePrice = parseFloat(value);
    } else if (priceLabel.includes('Courier Fees (Inc. VAT)')) {
      priceCourierFeeIncVAT = parseFloat(value);
    } else if (priceLabel.includes('Courier Fee')) {
      priceCourierFee = parseFloat(value);
    }
  });

  const purchasePrices: IDealDetails['purchasePrices'] =
    purchasePricesRaw?.map((item) => {
      const { label: labelEn, label_ar: labelAr, value } = item || {};
      return {
        label: {
          en: labelEn,
          ar: labelAr,
        },
        value,
      };
    }) || [];

  const imagesList = assetImageUrls?.map((image) => {
    const { orginalImage: original = '', thumbImage = '' } = image || {};

    return {
      baseUrl: '',
      fileName: `${label}-${thumbImage}`,
      filePath: original,
      thumbnailPath: thumbImage,
    };
  });

  const items = gradeReviewRequestData?.map((item) =>
    formatGradeReviewRequestDetailsResponse(item)
  );

  const formattedAttributes = buildFormattedAttributes();

  return {
    appraisalCode,
    assetImageUrls,
    imagesList,
    assetName: {
      en: label,
      // ar: labelAr || label,
      ar: label,
      he: label,
    },
    assetNameLabel: label,
    attributes,
    brandName,
    categoryName,
    courierStatus,
    // TODO: Generate this from courierStatus
    courierStatusId: courierStatus,
    dealDate,
    deliveryDate,
    deviceModel,
    formattedAttributes,
    gradeReviewRequestData: items || [],
    imeiNumber,
    invoices,
    isAutomatedAssessment,
    isDelivered,
    isReturned,
    itemDetailGrade: dealerPercentValue || percentValue,
    mobileGrade,
    myBidOnDealDate,
    paymentType,
    priceCourierFee,
    priceCourierFeeIncVAT,
    priceMyPurchasePrice,
    purchasePrices,
    shipmentNumber,
    shipmentTrackingUrl,
    statusLabels,
    storage,
    totalAmount,
    vatTypeName: formattedVatType,
  };
}
